'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Icon3rdGenGroup } from '@/components/icons';
import { cn } from '@/lib/utils';
import { companies, servicesByCompany } from '@/lib/data';
import type { Service, Product } from '@/lib/types';


const mainNavLinks = [
  { href: '/about', label: 'About' },
  { href: '/careers', label: 'Careers' },
  { href: '/news', label: 'News' },
  { href: '/contact', label: 'Contact' },
];

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  const NavLink = ({ href, children }: { href: string, children: React.ReactNode }) => (
    <Link
      href={href}
      className={cn(
        "text-sm font-medium transition-colors hover:text-primary",
        pathname.startsWith(href) ? "text-primary" : "text-muted-foreground"
      )}
      onClick={() => setIsMobileMenuOpen(false)}
    >
      {children}
    </Link>
  );
  
  const CompanyDropdown = ({ company }: { company: typeof companies[0] }) => {
    const items = servicesByCompany[company.name] || [];
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className={cn(
            "text-sm font-medium transition-colors hover:text-primary hover:bg-transparent p-0 h-auto",
            pathname.startsWith(company.path) ? "text-primary" : "text-muted-foreground"
          )}>
            {company.name}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem asChild>
            <Link href={company.path}>Overview</Link>
          </DropdownMenuItem>
          {items.map((item) => (
            <DropdownMenuItem key={item.slug} asChild>
              <Link href={`${company.path}/${'services' in company ? 'services' : 'products'}#${item.slug}`}>
                {'title' in item ? item.title : item.name}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const DesktopNav = () => (
    <nav className="hidden md:flex items-center gap-6">
      {companies.map(c => <CompanyDropdown key={c.name} company={c} />)}
      {mainNavLinks.map(link => <NavLink key={link.href} href={link.href}>{link.label}</NavLink>)}
    </nav>
  );
  
  const MobileNav = () => (
    <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
      <SheetTrigger asChild className="md:hidden">
        <Button variant="ghost" size="icon">
          <Menu />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left">
        <div className="flex flex-col gap-6 p-6">
          <Link href="/" onClick={() => setIsMobileMenuOpen(false)}>
            <Icon3rdGenGroup />
            <span className="sr-only">Home</span>
          </Link>
          <div className="flex flex-col gap-4">
             {companies.map(company => (
               <div key={company.name} className="flex flex-col gap-2">
                 <h3 className="font-bold">{company.name}</h3>
                 <Link href={company.path} onClick={() => setIsMobileMenuOpen(false)} className="pl-2 text-muted-foreground hover:text-primary">Overview</Link>
                 {(servicesByCompany[company.name] || []).map(item => (
                   <Link key={item.slug} href={`${company.path}/${'services' in company ? 'services' : 'products'}#${item.slug}`} onClick={() => setIsMobileMenuOpen(false)} className="pl-2 text-muted-foreground hover:text-primary">
                     {'title' in item ? item.title : item.name}
                   </Link>
                 ))}
               </div>
             ))}
          </div>
          <hr/>
          <div className="flex flex-col gap-4">
            {mainNavLinks.map(link => (
              <NavLink key={link.href} href={link.href}>{link.label}</NavLink>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );


  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link href="/" className="flex items-center gap-2">
          <Icon3rdGenGroup />
        </Link>
        <DesktopNav />
        <div className="flex items-center gap-2">
          <Button asChild className="hidden md:flex">
            <Link href="/contact">Get a Quote</Link>
          </Button>
          <MobileNav />
        </div>
      </div>
    </header>
  );
}
